import { useMemo, useState } from 'react'

import { CommandKind, type EngineState, type ExchangeConnection } from '@/lib/types'
import { exchangeLabel, exchangeColor, formatUsd, fundingPct } from '@/lib/format'
import { Spinner } from '@/components/common/Spinner'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { InfoHint } from '@/components/common/InfoHint'
import { ChevronDownIcon, PlugIcon, LockIcon } from '@/components/common/icons'
import type { AutobotHook } from './useAutobot'

interface ApiConnectionsProps {
  engine: EngineState
  exchanges: string[]
  run: AutobotHook['run']
  pending: string | null
}

export function ApiConnections({ engine, exchanges, run, pending }: ApiConnectionsProps) {
  const [open, setOpen] = useState(false)
  const [resetEx, setResetEx] = useState<string | null>(null)

  const byExchange = useMemo(() => {
    const map = new Map<string, ExchangeConnection>()
    for (const c of engine.connections) map.set(c.exchange, c)
    return map
  }, [engine.connections])

  // Биржи с активной ногой — сброс по ним блокируется (защита капитала).
  const busyExchanges = useMemo(() => {
    const s = new Set<string>()
    for (const t of engine.active_trades) {
      s.add(t.long_exchange)
      s.add(t.short_exchange)
    }
    return s
  }, [engine.active_trades])

  const connectedCount = engine.connections.filter((c) => c.connected).length

  return (
    <div className="panel">
      <button
        type="button"
        className="panel__head panel__head--btn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <div>
          <div className="panel__title">API-подключения</div>
          <div className="panel__subtitle">
            {connectedCount} из {exchanges.length} подключено · ключи задаются через ENV
          </div>
        </div>
        <span className={`collapse-caret${open ? ' is-open' : ''}`}>
          <ChevronDownIcon size={18} />
        </span>
      </button>

      {open ? (
        <div className="panel__pad">
          <div className="conn-grid">
            {exchanges.map((ex) => (
              <ConnectionCard
                key={ex}
                exchange={ex}
                conn={byExchange.get(ex)}
                busy={busyExchanges.has(ex)}
                pending={pending}
                onReset={() => setResetEx(ex)}
              />
            ))}
          </div>

          <button
            type="button"
            className="btn btn--sm conn-connect-all"
            disabled={pending === 'connect'}
            onClick={() => void run('connect', CommandKind.CONNECT)}
          >
            {pending === 'connect' ? <Spinner size={14} className="btn__spin" /> : <PlugIcon size={15} />}
            Подключить все
          </button>

          <TelegramStub />
        </div>
      ) : null}

      <ConfirmDialog
        open={resetEx !== null}
        title={`Сбросить ${resetEx ? exchangeLabel(resetEx) : ''}?`}
        message="Подключение биржи будет сброшено (статус «не подключено»). Активные связки на этой бирже должны быть предварительно закрыты."
        confirmLabel="Сбросить"
        busy={pending === 'reset'}
        onCancel={() => setResetEx(null)}
        onConfirm={() => {
          const ex = resetEx
          setResetEx(null)
          if (ex) void run('reset', CommandKind.RESET, { exchange: ex })
        }}
      />
    </div>
  )
}

interface ConnectionCardProps {
  exchange: string
  conn: ExchangeConnection | undefined
  busy: boolean
  pending: string | null
  onReset: () => void
}

function ConnectionCard({ exchange, conn, busy, pending, onReset }: ConnectionCardProps) {
  const connected = conn?.connected ?? false
  const hasError = !!conn?.error
  const statusCls = hasError ? 'neg' : connected ? 'pos' : 'neutral'
  const statusLabel = hasError ? 'Ошибка' : connected ? 'Подключено' : 'Не подключено'

  return (
    <div className="card conn-card">
      <div className="conn-card__head">
        <span className="ex-chip">
          <span className="ex-chip__dot" style={{ background: exchangeColor(exchange) }} />
          {exchangeLabel(exchange)}
        </span>
        <span className={`badge badge--${statusCls} badge--dot`}>{statusLabel}</span>
      </div>

      <dl className="conn-card__rows">
        <div className="conn-row">
          <dt>Баланс (USDT)</dt>
          <dd className="tnum">{formatUsd(conn?.balance_usdt ?? null)}</dd>
        </div>
        <div className="conn-row">
          <dt>
            Комиссия taker / maker
            <InfoHint text="Комиссии кешируются при подключении и входят в расчёт PnL (§1.9)." />
          </dt>
          <dd className="tnum">
            {fundingPct(conn?.taker_rate ?? null, 4)} / {fundingPct(conn?.maker_rate ?? null, 4)}
          </dd>
        </div>
        {hasError ? (
          <div className="conn-row conn-row--err">
            <dt>Ошибка</dt>
            <dd>{conn?.error}</dd>
          </div>
        ) : null}
      </dl>

      <div className="conn-card__actions">
        <button
          type="button"
          className="btn btn--sm btn--block"
          disabled={busy || pending === 'reset'}
          title={busy ? 'Сначала закройте активные связки на этой бирже' : undefined}
          onClick={onReset}
        >
          Сбросить
        </button>
      </div>
    </div>
  )
}

/** Заглушка Telegram (§1.12/§1.14 R2): поля оповещений (21a) и управления (21b). */
function TelegramStub() {
  return (
    <div className="tg-stub">
      <div className="tg-stub__head">
        <LockIcon size={15} />
        <span>Telegram (оповещения · управление)</span>
        <span className="badge badge--neutral">этап 21a / 21b</span>
      </div>
      <p className="tg-stub__note">
        Bot-token задаётся через <strong>ENV</strong> (в UI не вводится), список разрешённых
        <code> chat_id</code> — в конфиге. Канал оповещений активируется на этапе 21a (до live),
        управление — 21b.
      </p>
      <div className="tg-stub__fields">
        <div className="field">
          <span className="field__label">Bot token</span>
          <div className="input input--readonly">
            <input type="text" value="из ENV (FRAM_TELEGRAM_BOT_TOKEN)" readOnly disabled />
          </div>
        </div>
        <div className="field">
          <span className="field__label">Allowlist chat_id</span>
          <div className="input input--readonly">
            <input type="text" placeholder="напр. 12345678, 87654321" readOnly disabled />
          </div>
        </div>
      </div>
    </div>
  )
}
